import {
  type EngagementCandidate,
  type EngagementResolution,
  type EngagementVersion,
  engagementsActiveOn,
  resolveEngagementCandidates,
  resolveEngagementForEntry,
} from '@clockwise/shared';

/**
 * The engagement rule (`@clockwise/shared`) applied to a WEEK-GRID ROW.
 *
 * A row is a (customer, engagement, activity) triple spanning up to seven
 * days, and the rule is defined per ENTRY — per date. The two meet here.
 *
 * Rows bind, entries store. So the row's state is the worst state any of its
 * days is in: one ambiguous day makes the whole row ambiguous, because the
 * user has to answer before the week can be submitted and the row is where
 * they answer. A row that owns no entries yet (a freshly added line the user
 * is still filling in) resolves against `fallbackDate` — the week's Monday —
 * so the engagement can be settled BEFORE any hours are typed, and the create
 * carries it rather than patching it in afterwards.
 */
export function resolveRowEngagement(
  versions: readonly EngagementVersion[],
  row: {
    customerId: string | null;
    productId: string | null;
    engagementId: string | null;
    dates: readonly string[];
  },
  fallbackDate: string,
): EngagementResolution {
  const dates = row.dates.length > 0 ? row.dates : [fallbackDate];
  let bound: EngagementResolution | null = null;
  let sawNone = false;
  for (const date of dates) {
    const resolution = resolveEngagementForEntry(versions, {
      customerId: row.customerId,
      date,
      activityId: row.productId,
      currentEngagementId: row.engagementId,
    });
    // First `ask` wins outright — nothing later can make the row unambiguous.
    if (resolution.kind === 'ask') return resolution;
    if (resolution.kind === 'pending') return resolution;
    if (resolution.kind === 'bound') bound ??= resolution;
    if (resolution.kind === 'none') sawNone = true;
  }
  // A row whose engagement covers some days but not others (an engagement that
  // ended mid-week) reads as BOUND, not none: the binding is real for the days
  // it covers, and the days it doesn't are a separate problem the cell-level
  // guard surfaces. Reporting the row as unattributed would hide the binding
  // the user did make.
  if (bound) return bound;
  return sawNone ? { kind: 'none' } : { kind: 'pending' };
}

/**
 * The engagements MATCHING the row (its activity is listed, or the engagement
 * lists nothing), deduped across the row's dates.
 *
 * `resolveRowEngagement` collapses the same information into a verdict; this
 * returns the set itself, because the picker sections its dropdown by it — the
 * matching engagements above the rest — in every state, not only when the row
 * is ambiguous.
 */
export function engagementCandidatesForRow(
  versions: readonly EngagementVersion[],
  row: { customerId: string | null; productId: string | null; dates: readonly string[] },
  fallbackDate: string,
): EngagementCandidate[] {
  if (row.customerId == null || row.productId == null) return [];
  const dates = row.dates.length > 0 ? row.dates : [fallbackDate];
  const byId = new Map<string, EngagementCandidate>();
  for (const date of dates) {
    for (const c of resolveEngagementCandidates(versions, {
      customerId: row.customerId,
      date,
      activityId: row.productId,
    })) {
      if (!byId.has(c.engagementId)) byId.set(c.engagementId, c);
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Every engagement active for the row's customer on any of its dates,
 * regardless of whether it lists the row's activity.
 *
 * This is the OVERRIDE list behind "Change" on an already-bound row. Picking
 * an engagement that doesn't list the activity is a legitimate thing to record
 * — it is what "out of scope" means — so the override deliberately offers more
 * than the rule's candidate set. The write guard allows exactly this: any
 * engagement of the row's own customer that covers the date.
 */
export function engagementsActiveForRow(
  versions: readonly EngagementVersion[],
  row: { customerId: string | null; dates: readonly string[] },
  fallbackDate: string,
): EngagementCandidate[] {
  if (row.customerId == null) return [];
  const dates = row.dates.length > 0 ? row.dates : [fallbackDate];
  const byId = new Map<string, EngagementCandidate>();
  for (const date of dates) {
    // A wildcard activity id: every version's `activityIds` either contains it
    // (never) or is empty (always matches), so passing a sentinel would filter
    // wrongly. Match on date alone by asking for each version's own activity.
    for (const candidate of engagementsActiveOn(versions, {
      customerId: row.customerId,
      date,
    })) {
      if (!byId.has(candidate.engagementId)) byId.set(candidate.engagementId, candidate);
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}
